import React, { useState } from 'react';
import { Facebook, Instagram } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// X (formerly Twitter) icon component
const XIcon = ({ className, style }) => (
  <svg 
    viewBox="0 0 24 24" 
    className={className} 
    style={style}
    fill="currentColor"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const NewsletterSignup = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    try {
      const response = await fetch(`${API_URL}/api/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      
      if (data.success) {
        setStatus('subscribed');
        setMessage(data.message);
        setEmail('');
      } else {
        setStatus('error');
        setMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Something went wrong. Please try again.');
    }
    
    setTimeout(() => {
      setStatus('');
      setMessage('');
    }, 4000);
  };

  return (
    <div>
      <p className="mb-3 text-sm" style={{ color: '#c2a357' }}>
        Bi-weekly newsletter with brewing tips & exclusive offers
      </p>
      {status === 'subscribed' ? (
        <p className="text-sm font-bold" style={{ color: '#3a692b' }}>
          {message || "Welcome to the Mudbutt Brew family!"}
        </p>
      ) : status === 'error' ? (
        <p className="text-sm font-bold" style={{ color: '#d9534f' }}>
          {message || "Something went wrong. Please try again."}
        </p>
      ) : (
        <form onSubmit={handleSubmit} className="flex gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
            className="flex-1 px-3 py-2 rounded text-sm"
            style={{ backgroundColor: '#f5e9d0', color: '#5e3a1c', border: '1px solid #c2a357' }}
          />
          <button
            type="submit"
            className="px-4 py-2 rounded text-sm font-bold hover:opacity-80 transition-opacity"
            style={{ backgroundColor: '#c2a357', color: '#5e3a1c' }}
          >
            Join
          </button>
        </form>
      )}
    </div>
  );
};

export const Footer = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer style={{ backgroundColor: '#5e3a1c', color: '#f5e9d0' }}>
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_leaf-lounge-3/artifacts/kbshdl1e_file_00000000646471fdbb03789bab2f6e30.png" 
                alt="Mudbutt Brew"
                className="h-16 w-auto"
              />
            </div>
            <p style={{ color: '#c2a357' }}>
              Artisanal herbal brews from Elma, Washington. Rooted in tradition, crafted with nature.
            </p>
          </div>

          <div>
            <h3 className="font-bold text-lg mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => scrollToSection('products')}
                  className="hover:opacity-70 transition-opacity"
                  style={{ color: '#c2a357' }}
                >
                  Our Brews
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('benefits')}
                  className="hover:opacity-70 transition-opacity"
                  style={{ color: '#c2a357' }}
                >
                  Why Choose Us
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('about')}
                  className="hover:opacity-70 transition-opacity"
                  style={{ color: '#c2a357' }}
                >
                  Our Story
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="hover:opacity-70 transition-opacity"
                  style={{ color: '#c2a357' }}
                >
                  Contact
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-lg mb-4">Customer Service</h3>
            <ul className="space-y-2" style={{ color: '#c2a357' }}>
              <li className="hover:opacity-70 transition-opacity cursor-pointer">Shipping Info</li>
              <li className="hover:opacity-70 transition-opacity cursor-pointer">Returns Policy</li>
              <li className="hover:opacity-70 transition-opacity cursor-pointer">FAQ</li>
              <li className="hover:opacity-70 transition-opacity cursor-pointer">Brewing Guide</li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-lg mb-4">Connect With Us</h3>
            <div className="flex gap-4 mb-4">
              <a
                href="https://www.facebook.com/share/18QeAGPV7D/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full hover:opacity-70 transition-opacity border-2"
                style={{ backgroundColor: '#8b5c2f', borderColor: '#c2a357' }}
              >
                <Facebook className="w-5 h-5" style={{ color: '#f5e9d0' }} />
              </a>
              <a
                href="https://www.instagram.com/mudbuttbrew"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full hover:opacity-70 transition-opacity border-2"
                style={{ backgroundColor: '#8b5c2f', borderColor: '#c2a357' }}
              >
                <Instagram className="w-5 h-5" style={{ color: '#f5e9d0' }} />
              </a>
              <a
                href="https://x.com/muddbuttbrew"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full hover:opacity-70 transition-opacity border-2"
                style={{ backgroundColor: '#8b5c2f', borderColor: '#c2a357' }}
              >
                <XIcon className="w-5 h-5" style={{ color: '#f5e9d0' }} />
              </a>
            </div>
            <NewsletterSignup />
          </div>
        </div>

        <div
          className="border-t pt-8"
          style={{ borderColor: '#c2a357' }}
        >
          {/* Brew Notes */}
          <div className="mb-6 p-6 rounded-lg" style={{ backgroundColor: 'rgba(194, 163, 87, 0.1)', borderLeft: '4px solid #c2a357' }}>
            <h3 className="font-bold text-lg mb-3" style={{ color: '#f5e9d0' }}>Brew Notes</h3>
            <p className="text-sm leading-relaxed" style={{ color: '#c2a357' }}>
              <strong>Standard Brewing Instructions:</strong> Steep 1 tablespoon per 8 oz of hot water (195°F) for 15 minutes, covered. Strain and enjoy. 
              Please refer to individual product labels for any blend-specific brewing recommendations.
            </p>
          </div>

          {/* FDA Disclaimer */}
          <div className="mb-6 p-6 rounded-lg" style={{ backgroundColor: 'rgba(194, 163, 87, 0.1)', borderLeft: '4px solid #c2a357' }}>
            <h3 className="font-bold text-lg mb-3" style={{ color: '#f5e9d0' }}>Important Disclaimer</h3>
            <p className="text-sm leading-relaxed" style={{ color: '#c2a357' }}>
              These teas are traditional herbal supports—not medicines. Statements have not been evaluated by the Food and Drug Administration. 
              This product is not intended to diagnose, treat, cure, or prevent any disease. Consult your healthcare provider before use, 
              especially if pregnant, nursing, on medications, or with health conditions.
            </p>
          </div>
          
          <p className="text-center" style={{ color: '#c2a357' }}>
            &copy; 2024 Mudbutt Brew. All rights reserved. Crafted with care in Elma, Washington.
          </p>
          <div className="text-center mt-4">
            <a
              href="/admin"
              className="text-xs opacity-50 hover:opacity-100 transition-opacity"
              style={{ color: '#c2a357' }}
            >
              Admin
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
